import React, { useState, useCallback } from "react";
import clsx from "clsx";
import { MenuItem } from '../redux/slices/menuItemsSlice';
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface CategoryFilterProps {
  menuItems: readonly MenuItem[];
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;
}

interface Subcategory {
  name: string;
  count: number;
}

interface CategoryHierarchy {
  name: string;
  count: number;
  subcategories: Subcategory[];
}

// ✅ 1. Extracted AllItemsButton Component
interface AllItemsButtonProps {
  isActive: boolean;
  onClick: () => void;
}

const AllItemsButton = React.memo<AllItemsButtonProps>(({ isActive, onClick }) => (
  <button
    onClick={onClick}
    aria-pressed={isActive}
    className={clsx(
      "px-4 pt-3 pb-3 text-sm font-semibold whitespace-nowrap transition-all uppercase",
      isActive 
        ? "border-b-2 border-solid" 
        : "text-gray-600 hover:text-gray-900"
    )}
    style={isActive ? { color: '#EF4421', borderBottomColor: '#EF4421' } : undefined}
  >
    All Items
  </button>
));

AllItemsButton.displayName = 'AllItemsButton';

// ✅ 1. Extracted MainCategoryButton Component
interface MainCategoryButtonProps {
  category: CategoryHierarchy;
  isActive: boolean;
  onClick: () => void;
}

const MainCategoryButton = React.memo<MainCategoryButtonProps>(({ 
  category, 
  isActive, 
  onClick 
}) => {
  return (
    <button
      onClick={onClick}
      aria-pressed={isActive}
      className={clsx(
        "px-4 pt-3 pb-3 text-sm font-semibold whitespace-nowrap transition-all uppercase",
        isActive 
          ? "border-b-2 border-solid" 
          : "text-gray-600 hover:text-gray-900"
      )}
      style={isActive ? { color: '#EF4421', borderBottomColor: '#EF4421' } : undefined}
    >
      {category.name}
    </button>
  );
});

MainCategoryButton.displayName = 'MainCategoryButton';

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  menuItems,
  selectedCategory,
  onCategoryChange,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  
  // Check scroll position to show/hide arrows
  const checkScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);
  
  // Scroll left handler
  const handleScrollLeft = useCallback(() => {
    containerRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  }, []);
  
  // Scroll right handler
  const handleScrollRight = useCallback(() => {
    containerRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
  }, []);
  
  // Auto-scroll to active category
  React.useEffect(() => {
    if (!containerRef.current || !selectedCategory) return;
    
    const activeButton = containerRef.current.querySelector(
      `[data-category="${selectedCategory}"]`
    ) as HTMLElement;
    
    if (activeButton) {
      activeButton.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }
  }, [selectedCategory]);

  /** ✅ Pre-filter valid items */
  const availableItems = React.useMemo(
    () =>
      menuItems.filter(
        (item) =>
          item.is_enabled &&
          item.is_item_verified
      ),
    [menuItems]
  );

  /** ✅ Build category hierarchy */
  const categoryHierarchy = React.useMemo(() => {
    const hierarchy = new Map<string, CategoryHierarchy>();

    availableItems.forEach((item) => {
      const mainCategory = item.level1_category || item.category;
      const subCategory = item.level2_category;
      if (!mainCategory) return;

      if (!hierarchy.has(mainCategory)) {
        hierarchy.set(mainCategory, { name: mainCategory, count: 0, subcategories: [] });
      }

      const category = hierarchy.get(mainCategory)!;
      category.count += 1;

      if (subCategory) {
        const existing = category.subcategories.find((s) => s.name === subCategory);
        existing ? (existing.count += 1) : category.subcategories.push({ name: subCategory, count: 1 });
      }
    });

    return Array.from(hierarchy.values())
      .map((cat) => ({
        ...cat,
        subcategories: cat.subcategories,
      }));
  }, [availableItems]);

  // ✅ 3. Moved to useCallback to avoid inline functions
  const handleAllItemsClick = React.useCallback(() => {
    onCategoryChange(null);
  }, [onCategoryChange]);

  // ✅ 3. Moved to useCallback to avoid inline functions
  const handleMainCategoryClick = React.useCallback((category: CategoryHierarchy) => {
    onCategoryChange(category.name);
  }, [onCategoryChange]);
  
  // Add scroll event listener and check on mount
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    checkScroll();
    container.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    
    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);
  
  // Re-check scroll when categories change
  React.useEffect(() => {
    checkScroll();
  }, [categoryHierarchy, checkScroll]);

  return (
    <div className="bg-white border-b-2 border-gray-200">
      <div className="flex items-center gap-2">
        {/* Left Arrow */}
        <button
          onClick={handleScrollLeft}
          disabled={!showLeftArrow}
          className={clsx(
            "p-2 transition-all duration-200 flex-shrink-0",
            showLeftArrow
              ? "text-gray-700 hover:text-gray-900"
              : "text-gray-300 cursor-not-allowed"
          )}
          aria-label="Scroll left"
        >
          <FiChevronLeft className="w-6 h-6" />
        </button>
        
        {/* Scrollable Categories Container */}
        <div ref={containerRef} className="flex-1 overflow-x-auto scroll-smooth scrollbar-hide">
          <div className="flex gap-6">
            {/* ✅ All Items */}
            <AllItemsButton
              isActive={selectedCategory === null}
              onClick={handleAllItemsClick}
            />

            {/* ✅ Main Categories Only */}
            {categoryHierarchy.map((cat) => (
              <div key={cat.name} data-category={cat.name}>
                <MainCategoryButton
                  category={cat}
                  isActive={selectedCategory === cat.name}
                  onClick={() => handleMainCategoryClick(cat)}
                />
              </div>
            ))}
          </div>
        </div>
        
        {/* Right Arrow */}
        <button
          onClick={handleScrollRight}
          disabled={!showRightArrow}
          className={clsx(
            "p-2 transition-all duration-200 flex-shrink-0",
            showRightArrow
              ? "text-gray-700 hover:text-gray-900"
              : "text-gray-300 cursor-not-allowed"
          )}
          aria-label="Scroll right"
        >
          <FiChevronRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default React.memo(CategoryFilter);
